import React, { FC, useCallback, useEffect, useState } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Modal as AntModal, Row, Select, Spin, Switch, Tag, Tooltip } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { getEventOfferContainterRoute } from 'routes/events/list';
import { Button, Dropdown, Form, Icon, Label, Modal, Table, Typography, message } from 'ui';
import BackLink from 'ui/BackLink';
import { formRules } from 'utils/formRules';

import { useLazyGetEventDetailQuery } from 'services/events/eventsApiService';
import {
  useDeleteOfferMutation,
  useLazyGetOfferQuery,
  useLazyGetOffersQuery,
  useUpdateOfferMutation,
} from 'services/promoter/promoterApiService';
import { setActiveEvent } from 'slices/eventsSlice';
import { useAppDispatch } from 'store/index';

import EventsLoadingState from 'containers/events-loading-state';

import { EventCreatedType } from 'types/events/eventTypes';
import { EventOffer, OfferType } from 'types/promoters/promoterTypes';

import s from './EventOffer.module.scss';
import { transformToEventOfferCreateReqType } from './eventOffer.util';

const { confirm } = AntModal;

const DISABLE_DROPDOWN_ITEMS = [
  { key: 'edit', label: <div className={s.dropdownItem}>Edit</div> },
  { key: 'show', label: <div className={s.dropdownItem}>Shows</div> },
  { key: 'disable', label: <div className={s.dropdownItem}>Disable</div> },
  { key: 'delete', label: <div className={s.dropdownItem}>Delete</div> },
];

const ENABLE_DROPDOWN_ITEMS = [
  { key: 'edit', label: <div className={s.dropdownItem}>Edit</div> },
  { key: 'show', label: <div className={s.dropdownItem}>Shows</div> },
  { key: 'disable', label: <div className={s.dropdownItem}>Enable</div> },
  { key: 'delete', label: <div className={s.dropdownItem}>Delete</div> },
];

const OfferTypeDescriptions: Record<OfferType, string> = {
  [OfferType.perDifferentShowNames]: 'Discount applies once per different show name',
  [OfferType.perMultipleNumberOfTickets]:
    'Discount applies per multiple number of tickets purchased',
  [OfferType.fixedDiscount]: 'Applies a fixed discount to the order',
  [OfferType.greaterOrEqualToNumberOfTickets]:
    'Applies when total tickets meet or exceed a threshold',
  [OfferType.fixedPriceOnAllTicketTypes]: 'Sets a fixed price for all ticket types',
  [OfferType.fixedPriceOnSpecificTicketTypes]: 'Sets a fixed price for specific ticket types only',
  [OfferType.perDifferentEvents]: 'Discount applies per different event',
};

type TableOffer = {
  id: string;
  title: string;
};

const EventOfferComponent: FC = () => {
  const { eventId } = useParams();
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [getEventDetail, { data: eventDetail }] = useLazyGetEventDetailQuery();
  const [getOffers, { isFetching, data: offers }] = useLazyGetOffersQuery();
  const [getOffer, { isFetching: loadingOffer }] = useLazyGetOfferQuery();
  const [deleteOffer] = useDeleteOfferMutation();
  const [showDisableOffer, setShowDisableOffer] = useState<boolean>(true);
  const [updateOffer, { isLoading: updating }] = useUpdateOfferMutation();

  const [openedDropdown, setOpenedDropdown] = useState<boolean>(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [loadingKeys, setLoadingKeys] = useState<React.Key[]>([]);
  const [detailsMap, setDetailsMap] = useState<Record<string, EventOffer | undefined>>({});

  const [showOfferData, setShowOfferData] = useState<EventOffer[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showRestrictions, setShowRestrictions] = useState<string[] | undefined>([]);
  const [openedRecord, setOpenedRecord] = useState<EventOffer | undefined>();

  const selectedShow = Form.useWatch('showItem', form);

  const isDisabled = showRestrictions?.includes(selectedShow);

  useEffect(() => {
    if (eventId) {
      getOffers({ eventId: eventId });
    }
  }, [eventId, getOffers]);

  const handleClose = () => {
    setShowModal(false);
    setShowRestrictions([]);
    setOpenedRecord(undefined);
  };

  const handleGetEditingEvent = useCallback(
    async (eventId: string) => {
      const res = await getEventDetail(eventId);

      if ('data' in res) {
        dispatch(setActiveEvent(res.data as EventCreatedType));
      }
    },
    [dispatch, getEventDetail],
  );
  useEffect(() => {
    if (eventId) {
      handleGetEditingEvent(eventId);
    }
  }, [eventId, handleGetEditingEvent]);

  const handleAddOrUpdate = () => {
    const prev = showRestrictions ?? [];

    const exists = prev.findIndex((item) => item === form.getFieldValue('showItem'));

    const updated: string[] =
      exists !== -1
        ? prev.map((item, idx) => (idx === exists ? selectedShow : item))
        : [...prev, selectedShow];

    setShowRestrictions!(updated);
  };

  const handleShowDelete = (item: string) => {
    const filtered = showRestrictions?.filter((i) => item.toLowerCase() !== i);
    setShowRestrictions!(filtered);
  };

  useEffect(() => {
    if (!offers) return;

    if (!showDisableOffer) {
      setShowOfferData(offers.offers.filter((item) => item.active === true));
    } else {
      setShowOfferData(offers.offers);
    }
  }, [offers, showDisableOffer]);

  const handleClickDropdownItems = async (event: { key: string }, record) => {
    setOpenedDropdown(false);
    if (event.key === 'delete') {
      showDeleteConfirm(record?.id);
    }

    if (event.key === 'edit') {
      setOpenedDropdown(false);
      navigate(getEventOfferContainterRoute(String(eventId || ''), String(record?.id || '')));
    }

    if (event.key === 'disable') {
      if (record.active === false && showOfferData?.some((item) => item.active === true)) {
        showDuplicateConfirm();
        return;
      }

      setOpenedDropdown(false);

      const messi = transformToEventOfferCreateReqType(record);
      const result = { ...messi, active: record.active ? false : true };
      const res = await updateOffer({ id: record.id ?? '', data: result });

      if ('data' in res) {
        setShowOfferData(res.data.offers);
        if (record.active) {
          message.success('Offer has been disabled.');
        } else {
          message.success('Offer has been enabled.');
        }
      }
    }
    if (event.key === 'show') {
      setOpenedDropdown(false);
      if (!detailsMap[record.id]) {
        fetchOfferDetails(record.id);
      }
      setShowModal(true);
      const temp = record?.restrictToShows.map((item) => item.showId);
      setShowRestrictions(temp);
      setOpenedRecord(record);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 80,
    },
    {
      title: 'Date From',
      dataIndex: 'dateFrom',
      key: 'dateFrom',
      width: 90,
      render: (row: string) => {
        const date = dayjs(row);
        return date.isValid() ? date.format('DD/MM/YYYY') : '';
      },
    },
    {
      title: 'Date To',
      dataIndex: 'dateTo',
      key: 'dateTo',
      width: 90,
      render: (row: string) => {
        const date = dayjs(row);
        return date.isValid() ? date.format('DD/MM/YYYY') : '';
      },
    },
    {
      title: 'Type',
      dataIndex: 'offerType',
      key: 'offerType',
      ellipsis: true,
      width: 220,
      render: (value: OfferType) => OfferTypeDescriptions[value] || '-',
    },
    {
      title: (
        <>
          <Tooltip title="Limit to how many tickets will be issued with this offer.">
            <InfoCircleOutlined style={{ marginRight: 6, fontSize: 14 }} />
          </Tooltip>
          Limit
        </>
      ),
      dataIndex: 'numberOfTicketsAvailable',
      key: 'numberOfTicketsAvailable',
      width: 50,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 60,
      render: (active: boolean) => {
        return <Tag color={active ? 'cyan' : 'error'}>{active ? 'Enabled' : 'Disabled'}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Dropdown
          className="no-row-click"
          items={record.active ? DISABLE_DROPDOWN_ITEMS : ENABLE_DROPDOWN_ITEMS}
          placement="bottomRight"
          handleItemClick={(event) => handleClickDropdownItems(event, record)}
          open={openedDropdown === record.id}
          onOpenChange={(open) => setOpenedDropdown(open ? record.id : null)}>
          <div className={s.dropdownButton}>
            <Icon name="verticalDots" color="#7d7da2" />
          </div>
        </Dropdown>
      ),
    },
  ];

  const ticketColumns = [
    {
      title: 'Ticket Name',
      dataIndex: ['ticketType', 'name'],
      key: 'ticketName',
    },
    {
      title: 'Price To Be Sold',
      dataIndex: 'priceToBeSold',
      key: 'price',
      render: (price: number | null) => (price != null ? `€${price}` : 'N/A'),
    },
  ];

  const ticketColumnsDiffEvent = [
    {
      title: 'Ticket Name',
      dataIndex: ['ticketType', 'name'],
      key: 'ticketName',
    },
    {
      title: 'Minimum Number Of Tickets to be bought',
      dataIndex: 'minimumNumberOfTicketsToBeBought',
      key: 'minNumTicket',
    },
    {
      title: 'Price To Be Sold',
      dataIndex: 'priceToBeSold',
      key: 'price',
      render: (price: number | null) => (price != null ? `€${price}` : 'N/A'),
    },
  ];

  const showColumns = [
    {
      title: 'Show Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
    },

    {
      title: 'Show Time',
      dataIndex: 'startTime',
      key: 'startTime',
    },

    {
      title: 'Action',
      key: 'action',
      render: (row) => (
        <div className={s.actionButtons}>
          <button type="button" onClick={() => handleShowDelete(row.showId)}>
            Delete
          </button>
        </div>
      ),
    },
  ].filter(Boolean) as any;

  const fetchOfferDetails = async (id: string) => {
    setLoadingKeys((prev) => [...prev, id]);

    try {
      const res = await getOffer({ offerId: id });
      if ('data' in res) {
        setDetailsMap((prev) => ({ ...prev, [id]: res?.data?.offer }));
      }
    } catch (err) {
      console.error('Failed to fetch offer details:', err);
    } finally {
      setLoadingKeys((prev) => prev.filter((key) => key !== id));
    }
  };

  const handleExpand = (expanded: boolean, record: TableOffer) => {
    if (expanded) {
      setExpandedRowKeys((prev) => [...prev, record.id]);

      // Only fetch if not already fetched
      if (!detailsMap[record.id]) {
        fetchOfferDetails(record.id);
      }
    } else {
      setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
    }
  };

  const showDuplicateConfirm = useCallback(
    () => {
      confirm({
        title: 'Only one offer can be active at a time.',
        icon: <InfoCircleOutlined />,
        content: '',
        centered: true,
        mask: true,
        okText: 'OK',
        cancelButtonProps: { style: { display: 'none' } }, // Hide cancel button
      });
    },
    [], // Make sure `eventId` and `navigate` are in scope
  );

  const goToOfferCreation = useCallback(() => {
    if (showOfferData?.some((item) => item.active === true)) {
      showDuplicateConfirm();
    } else {
      navigate(getEventOfferContainterRoute(String(eventId || ''), 'create'));
    }
  }, [showOfferData, eventId, navigate, showDuplicateConfirm]);

  const showDeleteConfirm = async (id: string) => {
    confirm({
      title: 'Would you like to delete this offer?',
      icon: <InfoCircleOutlined />,
      content: '',
      centered: true,
      mask: true,
      async onOk() {
        if (id) {
          const res = await deleteOffer(id);
          if ('data' in res) {
            setShowOfferData(res.data.offers);
            message.success('Successfully deleted offer.');
          }
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleSubmit = useCallback(async () => {
    const tempShows = showRestrictions?.map((item) => ({ showId: item.toLowerCase() }));

    if (!openedRecord) {
      return;
    }
    const messi = transformToEventOfferCreateReqType(openedRecord);
    const result = { ...messi, showRestrictions: tempShows };
    const res = await updateOffer({ id: openedRecord?.id ?? '', data: result });

    if ('data' in res) {
      setShowOfferData(res.data.offers);

      message.success('Offer has been updated.');
      setShowModal(false);
      setShowRestrictions([]);
      setOpenedRecord(undefined);
      const res1 = await getOffer({ offerId: openedRecord?.id });
      if ('data' in res) {
        setDetailsMap((prev) => ({ ...prev, [openedRecord?.id]: res1?.data?.offer }));
      }
    }
  }, [showRestrictions, getOffer, updateOffer, openedRecord]);

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.header}>
        <div className={s.titileHeader}>
          {eventDetail && (
            <Typography type="h2" weight={700}>
              &quot;{eventDetail?.basicInfo?.eventName ?? ''}&quot;&nbsp;Event Offers
            </Typography>
          )}
        </div>

        {/*<Breadcrumbs routes={BREADCRUMBS_ROUTES} className={s.breadcrumbs} />*/}

        <div className={s.searchBlock}>
          <div>
            <Button onClick={goToOfferCreation} size="large" className={s.createBtn}>
              +&nbsp; CREATE OFFER
            </Button>
          </div>
        </div>
      </div>
      <Row align="middle" style={{ gap: 8 }}>
        <Typography type="main" weight={600} className={s.noMarginLable}>
          {showDisableOffer ? 'Hide disabled offers' : 'Show disabled offers'}
        </Typography>
        <Switch checked={showDisableOffer} onChange={(checked) => setShowDisableOffer(checked)} />
      </Row>

      <div style={{ overflowY: 'auto', marginTop: 16 }}>
        {showOfferData.length > 0 ? (
          <Table
            rowKey="id"
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
              expandedRowRender: (record) => {
                const details = detailsMap[record.id];
                const loading = loadingKeys.includes(record.id);

                if (loading || !details) {
                  return <Spin />;
                }

                return (
                  <div className={s.subTables}>
                    {details.ticketTypes.length > 0 && (
                      <div
                        style={
                          record?.offerType === OfferType.perDifferentEvents
                            ? { width: '60%' }
                            : { width: '40%' }
                        }>
                        <Typography type="h6">Ticket Types</Typography>
                        <Table
                          columns={
                            record?.offerType === OfferType.perDifferentEvents
                              ? ticketColumnsDiffEvent
                              : ticketColumns
                          }
                          dataSource={details.ticketTypes}
                          rowKey="id"
                          size="small"
                          pagination={false}
                        />
                      </div>
                    )}

                    {details.restrictToShows?.length > 0 && (
                      <div style={{ width: '40%' }}>
                        <Typography type="h6">Show Restrictions</Typography>
                        <Table
                          columns={[
                            {
                              title: 'Show Date',
                              dataIndex: 'date',
                              key: 'date',
                              render: (date: string) => new Date(date).toLocaleDateString(),
                            },
                            {
                              title: 'Show Time',
                              dataIndex: 'showTime',
                              key: 'showTime',
                              render: (time: string) =>
                                new Date(time).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                }),
                            },
                          ]}
                          dataSource={details.restrictToShows}
                          rowKey="showId"
                          size="small"
                          pagination={false}
                        />
                      </div>
                    )}
                  </div>
                );
              },
            }}
            loading={isFetching || updating}
            dataSource={showOfferData || []}
            columns={columns}
          />
        ) : isFetching ? (
          <div className={s.emptyWrapper}>
            <EventsLoadingState size={240} />
          </div>
        ) : (
          <div className={s.emptyWrapper}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 12,
              }}>
              <Typography color="grey">You have no offer for this event yet</Typography>
              <Button onClick={goToOfferCreation} size="large" className={s.createBtn}>
                +&nbsp; CREATE OFFER
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal loading={loadingOffer} handleClose={handleClose} open={showModal} footer={false}>
        <Modal.Header className={s.header}>
          <Typography type="h3" weight={700}>
            View shows restrictions
          </Typography>
        </Modal.Header>
        <Modal.Content className={s.content}>
          <Label>
            Through this window you have the facility to restrict this specific offer to specific
            shows. If there is no show listed in this window then that means that this offer is
            available on any show that the user chooses.
          </Label>
          <Form form={form} className={s.form}>
            <div>
              {!!showRestrictions?.length && (
                <Table
                  dataSource={eventDetail?.basicInfo?.showDates?.filter((item) =>
                    showRestrictions?.some((r) => r === item.showId?.toLowerCase()),
                  )}
                  rowKey="showId"
                  columns={showColumns}
                />
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 16,
                marginTop: 20,
              }}>
              <div style={{ width: '100%', marginBottom: 20 }}>
                <Label required>Show</Label>
                <Form.Item rules={[formRules.required]} name="showItem">
                  <Select
                    className={s.ticketSelect}
                    options={eventDetail?.basicInfo?.showDates?.map((item) => ({
                      label: item.startDate,
                      value: item.showId?.toLowerCase(),
                    }))}
                    placeholder="Select shows"
                  />
                </Form.Item>
              </div>

              <Button disabled={isDisabled} htmlType="submit" fullWidth onClick={handleAddOrUpdate}>
                Add
              </Button>
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions className={s.footer}>
          <Button block size="large" isLoading={updating} onClick={handleSubmit}>
            Save
          </Button>
          <Button block size="large" type="primary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default EventOfferComponent;
